import React, { useEffect, useMemo, useState } from "react";
import SeoRelatedLinks from "../../../components/seo/SeoRelatedLinks.jsx";
import SeoHeader from "../../../components/seo/SeoHeader.jsx";
import SeoContactSection from "../../../components/seo/SeoContactSection.jsx";
import SeoWorkSteps from "../../../components/seo/SeoWorkSteps.jsx";

const sections = [
  {
    id: "check",
    label: "Er en gratis digital meny nok for restauranten din?",
  },
  {
    id: "definition",
    label: "Hva er en gratis digital meny?",
  },
  {
    id: "features",
    label: "Hva bør en gratis digital meny inneholde?",
  },
  {
    id: "why-free",
    label: "Hvorfor er en gratis start nyttig for restauranter?",
  },
  {
    id: "enough",
    label: "Når er en gratis plan nok?",
  },
  {
    id: "upgrade",
    label: "Når er gratisversjonen ikke nok lenger?",
  },
  {
    id: "pdf",
    label: "Gratis QR-meny eller PDF bak en QR-kode?",
  },
  {
    id: "sales",
    label: "Kan en gratis digital meny hjelpe med salg?",
  },
  {
    id: "test",
    label: "Hvordan teste en gratis digital meny på 30 minutter",
  },
  {
    id: "bestplace",
    label: "Hvordan Best Place hjelper deg med å starte gratis",
  },
  {
    id: "faq",
    label: "FAQ om gratis digitale menyer",
  },
];

const checklist = [
  "Du vil teste en QR-meny først.",
  "Restauranten din har en liten eller oversiktlig meny.",
  "En enkel start uten komplisert design er nok foreløpig.",
  "Du vil se om gjester bruker QR-koden.",
  "Du vil endre navn, priser eller tilgjengelighet raskere.",
  "Du er ikke sikker ennå på om du trenger et komplett restaurantsystem.",
  "Du vil ikke betale for et nytt verktøy med en gang.",
  "En enkel online meny uten komplisert automatisering er nok for første test.",
  "Du vil teste den digitale menyen på én lokasjon først.",
];

const CALLBACK_API_ORIGIN =
  typeof window !== "undefined" &&
  (window.location.hostname === "localhost" ||
    window.location.hostname === "127.0.0.1")
    ? "http://localhost:3030"
    : "";

function getResult(score) {
  if (score <= 3) {
    return {
      title: "En gratisversjon kan være for enkel",
      text:
        "Hvis du med en gang trenger reservasjoner, avansert design, eget domene, flere lokasjoner eller dypere automatisering, er en gratis plan kanskje ikke nok særlig lenge.",
      tone: "low",
    };
  }

  if (score <= 6) {
    return {
      title: "En gratis test gir mening",
      text:
        "For restauranten din kan en gratis digital meny være et godt første steg: test QR-koden, administrer en enkel meny, sjekk mobilvisningen og se hvordan gjestene reagerer.",
      tone: "medium",
    };
  }

  return {
    title: "En gratis plan er et sterkt utgangspunkt",
    text:
      "Restauranten din kan sannsynligvis teste uten stor risiko om en digital meny hjelper i daglig drift: med priser, bilder, tilgjengelighet, QR-tilgang og raskere oppdateringer.",
    tone: "high",
  };
}

function VisualIdea({ children }) {
  return (
    <div className="seoVisualIdea" aria-label="Visuell idé">
      <div className="seoVisualIdea__icon" aria-hidden="true">
        ✦
      </div>

      <div>
        <strong>Idé til illustrasjon:</strong>
        <p>{children}</p>
      </div>
    </div>
  );
}

export default function FreeDigitalMenuForRestaurantsNo() {
  const [checked, setChecked] = useState([]);

  const score = checked.length;
  const result = useMemo(() => getResult(score), [score]);

  useEffect(() => {
    document.documentElement.setAttribute("lang", "no");
    document.title =
      "Gratis digital meny for restauranter: test først, invester senere | Best Place";
  }, []);

  const toggle = (item) => {
    setChecked((prev) =>
      prev.includes(item) ? prev.filter((x) => x !== item) : [...prev, item]
    );
  };

  const scrollToSection = (id) => {
    document.getElementById(id)?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  };

  return (
    <main className="qrArticle">
      <SeoHeader
        lang="no"
        langLinks={{
          de: "/de/kostenlose-digitale-speisekarte",
          en: "/en/free-digital-menu-for-restaurants",
          no: "/no/gratis-digital-meny-for-restauranter",
        }}
      />

      <section className="qrArticle__hero">
        <div className="qrArticle__heroInner">
          <h1 className="qrArticle__heroTitle">
            <span>
              Gratis digital meny for restauranter: test først, invester senere
            </span>
          </h1>

          <div className="qrArticle__topsection">
            <div className="qrArticle__topsection--img-wrapper">
              <div className="qrArticle__topsection--img"></div>
            </div>

            <div className="qrArticle__topsection--text-wrapper">
              <p className="seoHero__lead">
                En gratis digital meny er ikke en billig erstatning for et ekte
                system. Den er et trygt første steg for å teste om en QR-kode-meny
                kan hjelpe i restaurantens daglige arbeid.
              </p>

              <p className="seoHero__text">
                Ikke alle restauranter trenger en stor programvarepakke med en
                gang. Men nesten alle restauranter kan teste om en digital meny
                ser bra ut på smarttelefon, om gjestene forstår QR-koden, og om
                oppdateringer blir enklere.
              </p>
            </div>
          </div>
        </div>
      </section>

      <SeoContactSection lang="no" apiOrigin={CALLBACK_API_ORIGIN} />

      <section className="articls">
        <h2>Innhold i artikkelen</h2>

        <nav className="qrArticle__nav" aria-label="Artikkelnavigasjon">
          <ol className="qrArticle__navList">
            {sections.map((item, index) => (
              <li key={item.id} className="qrArticle__navItem">
                <button
                  type="button"
                  className="qrArticle__navButton"
                  onClick={() => scrollToSection(item.id)}
                >
                  <span className="qrArticle__navNumber">
                    {String(index + 1).padStart(2, "0")}
                  </span>

                  <span className="qrArticle__navLabel">{item.label}</span>
                </button>
              </li>
            ))}
          </ol>
        </nav>
      </section>

      <article>
        <section id="check" className="qrArticle__quizWave">
          <div className="qrArticle__quizInner">
            <h2>Er en gratis digital meny nok for restauranten din?</h2>

            <p>
              Trykk på utsagnene som passer for restauranten din. Resultatet er
              bevisst ærlig: ikke alle restauranter trenger et komplekst digitalt
              oppsett fra dag én, men mange kan ha nytte av en enkel QR-meny mye
              tidligere enn de tror.
            </p>

            <div className="qrArticle__checkGrid">
              {checklist.map((item) => (
                <label key={item} className="qrArticle__checkItem">
                  <input
                    type="checkbox"
                    checked={checked.includes(item)}
                    onChange={() => toggle(item)}
                  />
                  <span className="text">{item}</span>
                </label>
              ))}
            </div>

            <div className={`qrArticle__result qrArticle__result--${result.tone}`}>
              <div className="qrArticle__score">
                {score} / {checklist.length}
              </div>

              <div>
                <strong>{result.title}</strong>
                <p>{result.text}</p>
              </div>
            </div>
          </div>
        </section>

        <section id="definition" className="qrArticle__quizWave">
          <div className="qrArticle__quizInner">
            <h2>Hva er en gratis digital meny?</h2>

            <p>
              En gratis digital meny er en online meny som en restaurant kan
              opprette og teste uten startkostnader. Gjester åpner den vanligvis
              via en QR-kode på bordet eller via en lenke på restaurantens
              nettside.
            </p>

            <p>
              Gratis bør ikke bety uprofesjonelt. Målet med en gratisversjon er
              ikke å løse alle problemer med en gang. Målet er å gi restauranten
              en trygg måte å teste den ekte prosessen på.
            </p>

            <h3>Test den ekte arbeidsflyten først</h3>

            <p>
              En gratis plan betyr: test den ekte arbeidsflyten først, og bestem
              deretter hva som er verdt å utvide. Det gir restauranten en
              praktisk måte å se om en digital meny passer i daglig drift før
              den velger et større system.
            </p>
          </div>
        </section>

        <section id="features" className="acquaintance">
          <h2>Hva bør en gratis digital meny inneholde?</h2>

          <p>
            En gratisversjon trenger ikke alle funksjoner. Men den bør gi nok
            verdi til at restauranten kan teste om ideen fungerer i praksis.
          </p>

          <ul className="qrArticle__block-li">
            <li className="text__seo-li">Opprette en meny.</li>
            <li className="text__seo-li">Legge til kategorier.</li>
            <li className="text__seo-li">Legge til navn på retter.</li>
            <li className="text__seo-li">Legge inn priser.</li>
            <li className="text__seo-li">Legge til beskrivelser.</li>
            <li className="text__seo-li">Teste bilder.</li>
            <li className="text__seo-li">Bruke en QR-kode.</li>
            <li className="text__seo-li">Sjekke mobilvisning.</li>
            <li className="text__seo-li">Redigere produkter.</li>
            <li className="text__seo-li">Skjule retter.</li>
            <li className="text__seo-li">Dele en enkel lenke.</li>
            <li className="text__seo-li">Kjøre en teamtest.</li>
          </ul>

          <p>
            Hvis du kan lage en meny selv, endre en pris og åpne den på en
            smarttelefon, har gratisversjonen allerede praktisk verdi.
          </p>

          <VisualIdea>
            En enkel gratis menytest: én kategori, noen få retter, priser,
            beskrivelser, en QR-kode og en smarttelefonvisning.
          </VisualIdea>
        </section>

        <section id="why-free" className="qrArticle__quizWave">
          <div className="qrArticle__quizInner">
            <h2>Hvorfor er en gratis start nyttig for restauranter?</h2>

            <p>
              Restauranter tar sjelden i bruk nye verktøy helt blindt. Det
              handler ikke bare om penger. Det handler også om tid, personalets
              vaner, gjestenes atferd og spørsmålet om folk faktisk vil bruke det
              nye verktøyet.
            </p>

            <p>
              Derfor er en liten test ofte smartere enn en stor umiddelbar
              endring. En restaurant kan starte med noen få produkter, teste
              QR-koden på ett bord og se om prosessen er tydelig.
            </p>

            <h3>En gratis test reduserer risiko</h3>

            <p>
              En gratis digital meny lar restauranten sjekke ideen før den tar
              en større beslutning. Eieren kan se om oppdateringer blir enklere,
              om QR-koden blir forstått, og om teamet kan jobbe med menyen.
            </p>
          </div>
        </section>

        <section id="enough" className="acquaintance">
          <h2>Når er en gratis plan nok?</h2>

          <p>
            En gratis plan kan være nok når du først vil teste praktisk verdi og
            ennå ikke trenger komplekse funksjoner.
          </p>

          <ul className="qrArticle__block-li">
            <li className="text__seo-li">Menyen din er oversiktlig.</li>
            <li className="text__seo-li">
              Du trenger først bare QR-tilgang.
            </li>
            <li className="text__seo-li">
              Du vil teste gjestenes reaksjoner.
            </li>
            <li className="text__seo-li">
              Du trenger ikke svært tilpasset design ennå.
            </li>
            <li className="text__seo-li">Du har én lokasjon.</li>
            <li className="text__seo-li">
              Du vil oppdatere priser og tilgjengelighet raskere.
            </li>
          </ul>

          <h3>Eksempel</h3>

          <p>
            En kafé med 25 produkter vil teste QR-koder på noen få bord: kaffe,
            desserter, frokost og sesongdrikker. For en slik start kan en gratis
            plan være helt nok.
          </p>
        </section>

        <section id="upgrade" className="qrArticle__quizWave">
          <div className="qrArticle__quizInner">
            <h2>Når er gratisversjonen ikke nok lenger?</h2>

            <p>
              En gratisversjon er et startpunkt. Den er ikke alltid den endelige
              løsningen. Når den digitale menyen blir en del av daglig drift, kan
              mer avanserte funksjoner bli nyttige.
            </p>

            <div className="seoComparison">
              <div className="seoComparison__col">
                <h3>Gratis plan er ofte nok for</h3>

                <ul className="qrArticle__block-li">
                  <li className="text__seo-li">Første test.</li>
                  <li className="text__seo-li">Liten meny.</li>
                  <li className="text__seo-li">Teste QR-tilgang.</li>
                  <li className="text__seo-li">Enkel smarttelefonvisning.</li>
                  <li className="text__seo-li">Første bilder og priser.</li>
                </ul>
              </div>

              <div className="seoComparison__col seoComparison__col--highlight">
                <h3>Oppgradering blir nyttig for</h3>

                <ul className="qrArticle__block-li">
                  <li className="text__seo-li">Egen subdomain-lenke.</li>
                  <li className="text__seo-li">Mer kontroll over design.</li>
                  <li className="text__seo-li">Online reservasjoner.</li>
                  <li className="text__seo-li">Flerspråklig meny.</li>
                  <li className="text__seo-li">Kampanjeområder.</li>
                  <li className="text__seo-li">
                    Mer kontroll over restaurantens offentlige presentasjon.
                  </li>
                </ul>
              </div>
            </div>

            <h3>Valider først, utvid etterpå</h3>

            <p>
              Å starte gratis betyr ikke at du må bli værende på gratisversjonen
              for alltid. Det betyr: valider først, og utvid deretter med et
              tydelig formål.
            </p>
          </div>
        </section>

        <section id="pdf" className="acquaintance">
          <h2>Gratis QR-meny eller PDF bak en QR-kode?</h2>

          <p>
            Mange restauranter starter med en PDF bak en QR-kode. Det er bedre
            enn ingenting, men det er ikke alltid den beste opplevelsen for
            gjester på smarttelefon.
          </p>

          <div className="seoComparison">
            <div className="seoComparison__col">
              <h3>PDF bak QR-kode</h3>

              <ul className="qrArticle__block-li">
                <li className="text__seo-li">Rask å lage.</li>
                <li className="text__seo-li">Ofte gratis.</li>
                <li className="text__seo-li">
                  Mindre behagelig på smarttelefon.
                </li>
                <li className="text__seo-li">Vanskeligere å oppdatere.</li>
                <li className="text__seo-li">
                  Svak håndtering av utsolgte produkter.
                </li>
              </ul>
            </div>

            <div className="seoComparison__col seoComparison__col--highlight">
              <h3>Digital meny</h3>

              <ul className="qrArticle__block-li">
                <li className="text__seo-li">Mobilvennlig.</li>
                <li className="text__seo-li">Tydelige kategorier.</li>
                <li className="text__seo-li">Enklere å oppdatere.</li>
                <li className="text__seo-li">Bilder og beskrivelser.</li>
                <li className="text__seo-li">Produkter kan skjules.</li>
                <li className="text__seo-li">
                  CTA til demo eller reservasjon er mulig.
                </li>
              </ul>
            </div>
          </div>

          <p>
            Med en QR-kode er ikke selve koden verdien. Den virkelige verdien er
            opplevelsen etter skanning: lesbarhet, hastighet, struktur og nyttig
            informasjon.
          </p>
        </section>

        <section id="sales" className="qrArticle__quizWave">
          <div className="qrArticle__quizInner">
            <h2>Kan en gratis digital meny hjelpe med salg?</h2>

            <p>
              En digital meny garanterer ikke høyere salg. Men den kan gjøre
              tilbud mer synlige: forretter, desserter, drikke, menyer, dagens
              tilbud eller sesonganbefalinger.
            </p>

            <p>
              Poenget er ikke aggressivt mersalg. Poenget er bedre synlighet. En
              gjest kan bare bestille det han eller hun legger merke til,
              forstår og synes virker fristende i riktig øyeblikk.
            </p>

            <h3>Synlighet er en del av menyopplevelsen</h3>

            <p>
              En enkel digital meny kan allerede hjelpe med å vise de riktige
              produktene tydeligere: anbefalinger, sesongtilbud, drikke,
              desserter eller tidsbegrensede produkter.
            </p>
          </div>
        </section>

        <section id="test" className="acquaintance">
          <h2>Hvordan teste en gratis digital meny på 30 minutter</h2>

          <p>
            Den første testen trenger ikke være perfekt. Den trenger bare å svare
            på tre spørsmål: Kan du redigere menyen selv? Forstår gjesten
            flyten? Er det verdt å fortsette?
          </p>

          <ol className="seoSteps">
            <li className="text__seo-li">Velg 5–10 populære produkter.</li>
            <li className="text__seo-li">
              Legg til navn, priser og korte beskrivelser.
            </li>
            <li className="text__seo-li">Legg til 2–3 bilder.</li>
            <li className="text__seo-li">Generer en QR-kode.</li>
            <li className="text__seo-li">
              Åpne menyen på smarttelefonen din.
            </li>
            <li className="text__seo-li">Vis den til ett teammedlem.</li>
            <li className="text__seo-li">
              Test QR-koden ved et ekte bord.
            </li>
            <li className="text__seo-li">
              Sjekk om neste handling er tydelig.
            </li>
            <li className="text__seo-li">
              Prøv å endre en pris eller skjule et produkt.
            </li>
          </ol>

          <VisualIdea>
            En 30-minutters testflyt: legg til noen ekte produkter, åpne
            QR-koden på en smarttelefon, vis den til teamet og gjør én liten
            menyoppdatering.
          </VisualIdea>
        </section>

        <div id="bestplace" className="qrArticle__anchorTarget">
          <SeoWorkSteps lang="no" />
        </div>

        <section id="faq" className="acquaintance">
          <h2>Vanlige spørsmål om gratis digitale menyer</h2>

          <div className="seoFaq">
            <details>
              <summary>Kan en digital meny virkelig være gratis?</summary>

              <p>
                Ja, en enkel versjon kan være gratis hvis leverandøren tilbyr en
                gratis plan for enkle menyer og QR-kode-tilgang.
              </p>
            </details>

            <details>
              <summary>Hva er ulempen med en gratisversjon?</summary>

              <p>
                Det bør ikke være noen skjult felle. Men det finnes vanligvis
                begrensninger: færre designvalg, mindre automatisering eller
                ingen avanserte funksjoner som reservasjoner.
              </p>
            </details>

            <details>
              <summary>Er en PDF med QR-kode nok?</summary>

              <p>
                For en veldig enkel start kan en PDF være nok. For daglig bruk
                på smarttelefon er en mobil digital meny vanligvis mer behagelig.
              </p>
            </details>

            <details>
              <summary>Når bør jeg bytte til en betalt versjon?</summary>

              <p>
                Når du trenger mer kontroll, egen subdomain, reservasjoner, mer
                design, språk eller markedsføringsfunksjoner.
              </p>
            </details>

            <details>
              <summary>Kan jeg starte uten utvikler?</summary>

              <p>
                Ja. Ideen med denne typen verktøy er at eiere eller
                administratorer kan administrere produkter, priser, bilder og
                beskrivelser selv.
              </p>
            </details>

            <details>
              <summary>Er dette nyttig for små restauranter?</summary>

              <p>
                Ja. Små restauranter har ofte nytte av å kunne gjøre
                menyendringer uten å starte en ny design- eller trykkeprosess
                hver gang.
              </p>
            </details>
          </div>
        </section>

        <SeoRelatedLinks
          lang="no"
          group="menu"
          exclude="/no/gratis-digital-meny-for-restauranter"
        />
      </article>
    </main>
  );
}